<script lang="ts">
	import type { Connections } from "./libs/sip/connections";
	import type { DialerAgentConfiguration } from "./libs/app/configuration";
	import type { Writable } from "svelte/store";

	import Confirmation from "./components/Confirmation/Confirmation.svelte";
	import Login from "./views/Login.svelte";
	import Logout from "./views/Logout.svelte";
	import Pause from "./views/Pause.svelte";
	import PrivateRouteGuard from "./views//PrivateRouteGuard.svelte";
	import Redirect from "./views/Redirect.svelte";
	import Summary from "./views/Summary.svelte";
	import Target from "./views/Target.svelte";
	import { DialerAgentStatus } from "./libs/app/dialer-agent-enums";
	import { Router, Route, navigate, Link } from "svelte-navigator";
	import { SvelteToast } from "@zerodevx/svelte-toast";
	import { confirmation } from "./components/Confirmation";
	import { dialerAgent, scheduledCalls } from "./bootstrap";
	import AddTarget from "./views/AddTarget.svelte";
	import type { ScheduledCall } from "./libs/app/scheduled-call";

	const connections: Writable<Connections> = dialerAgent.connections;
	const configuration: Writable<DialerAgentConfiguration> = dialerAgent.configuration;
	const status: Writable<DialerAgentStatus> = dialerAgent.status;

	/**
	 * Príznak, či sa má zobrazovať upozornenie na neaktívne linky
	 */
	let showInactiveWarning = false;
	dialerAgent.inactiveLinks.subscribe((inactiveLinks) => {
		showInactiveWarning = inactiveLinks;
	});

	let showScheduledCalls = false;
	let nextScheduledCall: ScheduledCall = null;
	scheduledCalls.subscribe((scheduledCalls: Array<ScheduledCall>) => {
		if (scheduledCalls.length > 0) {
			nextScheduledCall = scheduledCalls[0];
		} else {
			nextScheduledCall = null;
		}
	});

	function restart() {
		window.location.reload();
	}
</script>

<Router>
	{#if $configuration !== null}
		<header>
			<nav class="navbar navbar-expand-lg navbar-dark bg-primary">
				<div class="container-fluid">
					<div class="btn-group">
						<button
							class="btn btn-secondary"
							on:click|preventDefault={async () => {
								const result = await confirmation.show("Naozaj chcete prerušiť prácu?");
								if (result === false) {
									return false;
								}

								navigate("/main/pause");
							}}
							disabled={$status === DialerAgentStatus.Break}>Prestávka</button
						>

						<Link to="/main/add-target" class="btn btn-secondary {$status === DialerAgentStatus.Talk || $status === DialerAgentStatus.AfterCallWork ? 'disabled' : ''}">Pridať klienta kampane</Link>
					</div>

					{#if nextScheduledCall !== null}
					<div id="scheduled-call">
						<span>Najbližší naplánovaný hovor: <strong>{nextScheduledCall.time} - {nextScheduledCall.name} ({nextScheduledCall.campaign_name})</strong></span>
						<button class="btn btn-success" on:click|preventDefault={() => {
							showScheduledCalls = true;
						}}>Ďalšie</button>
					</div>
					{/if}

					<div class="btn-group">
						<button
							class="btn btn-secondary"
							on:click|preventDefault={async () => {
								const result = await confirmation.show("Naozaj sa chcete odhlásiť?");
								if (result === false) {
									return false;
								}

								navigate("/logout");
							}}>Odhlásenie</button
						>
					</div>
				</div>
			</nav>
		</header>

		<div class="bg-dark">
			<div class="container-fluid active-links">
				Linky:
				{#each Object.entries($connections) as [id, connection]}
					<span class="badge {connection.active === true ? 'bg-info' : ''}" title={connection.active ? "Aktívne" : "Neaktívne"}>
						{#if connection.active === true}
							<span><i class="fas fa-check-circle" /></span>
						{:else}
							<span><i class="fas fa-exclamation-triangle" /></span>
						{/if}
						{connection.name}
					</span>
				{/each}
			</div>
		</div>

		{#if showInactiveWarning}
			<div class="container mt-4">
				<div class="alert alert-danger">
					<div class="d-flex justify-content-between">
						<div class="mt-2">
							Linka
							{#each Object.entries($connections) as [id, connection]}
								{#if connection.active === false}
									{connection.name}
								{/if}
							{/each}
							nie je pripojená!
						</div>
						<button class="btn btn-primary" on:click={restart}>Pripojiť</button>
					</div>
				</div>
			</div>
		{/if}
	{/if}

	<main class="container-fluid">
		<Route path="/" primary={false}>
			<Login />
		</Route>

		<Route path="/main/*" primary={false}>
			<PrivateRouteGuard let:registerFocus>
				<Route path="/summary">
					<Summary />
				</Route>

				<Route path="/pause">
					<Pause />
				</Route>

				<Route path="/add-target">
					<AddTarget />
				</Route>

				<Route path="/target/:id/:caller" primary={false}>
					<Target />
				</Route>
			</PrivateRouteGuard>
		</Route>

		<Route path="/logout" primary={false}>
			<Logout />
		</Route>

		<Route path="/*" primary={false}>
			<Redirect />
		</Route>
	</main>
</Router>

<SvelteToast />

<Confirmation />

{#if showScheduledCalls === true}
	<div class="modal-backdrop-custom" />

	<div class="modal">
		<div class="modal-dialog modal-lg">
			<div class="modal-content">
				<div class="modal-header">Naplánované hovory</div>

				<div class="modal-body">
					<table class="table">
						<thead>
							<tr>
								<th>Čas</th>
								<th>Meno</th>
								<th>Telefón</th>
								<th>Kampaň</th>
							</tr>
						</thead>

						<tbody>
							{#each $scheduledCalls as scheduledCall}
							<tr>
								<td>{scheduledCall.time}</td>
								<td>{scheduledCall.name}</td>
								<td>{scheduledCall.telno}</td>
								<td>{scheduledCall.campaign_name}</td>
							</tr>
							{/each}
						</tbody>
					</table>
				</div>

				<div class="modal-footer">
					<button class="btn btn-info" on:click|preventDefault={() => {
						showScheduledCalls = false;
					}}>Zatvoriť</button>
				</div>
			</div>
		</div>
	</div>
{/if}

<audio id="remote-audio" class="d-none" controls>
	<track kind="captions" />
</audio>

<style lang="scss">
	:global(body) {
		font-family: sans-serif;
	}

	.active-links {
		color: #fff;
	}

	.active-links .badge {
		margin-left: 0.75em;
		margin-bottom: 4px;
	}

	#scheduled-call > span {
		display: inline-block;
		margin-right: 1em;
	}

	.modal-backdrop-custom:before {
		content: "";
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: #000;
		opacity: 0.7;
	}

	.modal {
		display: block;
	}
</style>
